// Phase 6 — small helpers shared by the cooking screen.
//
// None of these touch the cookpit task projection; they are pure
// display helpers. Wall-clock arithmetic uses the client clock corrected
// by the server-issued `clockOffset` (README §10.1 — authoritative
// time).

export function formatHHMMSS(totalSeconds: number): string {
  // Negative values are clamped to zero — the display never shows a
  // negative duration. Sign is the caller's responsibility (e.g. a
  // countdown prefixes with a minus sign when it has expired).
  const safe = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function formatHHMM(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const d = new Date(t);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function correctedNow(clockOffset: number): number {
  return Date.now() + clockOffset;
}

// Human "how long ago" for an overdue task, calm and compact:
//   < 1 min  → "just now"
//   < 1 hour → "Nm ago"
//   else     → "Hh Mm ago"
// Negative/zero clamps to "just now". Used by the outstanding panel.
export function formatOverdue(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  if (safe < 60) return 'just now';
  const totalMin = Math.floor(safe / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m ago` : `${m}m ago`;
}

// Parse ISO 8601 / clock-style duration to seconds. Returns 0 on
// parse failure so an unexpectedly-shaped string never blows up
// the display. Mirrors the function-side parser in
// `apps/functions/cookpit/projection.ts`.
export function durationToSeconds(d: string | undefined | null): number {
  if (!d) return 0;
  const clock = /^(\d{2,}):(\d{2}):(\d{2})$/.exec(d);
  if (clock) {
    const [, hh, mm, ss] = clock;
    return parseInt(hh!, 10) * 3600 + parseInt(mm!, 10) * 60 + parseInt(ss!, 10);
  }
  const iso = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(d);
  if (!iso) return 0;
  const [, dd, hh, mm, ss] = iso;
  const days = dd ? parseInt(dd, 10) : 0;
  const hours = hh ? parseInt(hh, 10) : 0;
  const mins = mm ? parseInt(mm, 10) : 0;
  const secs = ss ? parseFloat(ss) : 0;
  return ((days * 24 + hours) * 60 + mins) * 60 + secs;
}

// Format a duration in seconds as a compact "HHh:MMm" badge, e.g.
//   1800  → "00h:30m"   (PT30M)
//   5400  → "01h:30m"   (PT1H30M)
// Used for the prep / total time chips on the prereqs screen — friendlier
// than the raw ISO-8601 string. Days fold into hours; sub-minute remainders
// round to the nearest minute. Negative values clamp to zero.
export function formatDurationHM(totalSeconds: number): string {
  const totalMin = Math.max(0, Math.round(totalSeconds / 60));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(h)}h:${pad(m)}m`;
}
